/**
 * Cognito Authentication Service
 * Handles AWS Cognito authentication via Amplify
 */

import { IAuthService, User, SignupResult, LoginResult } from './IAuthService';

export class CognitoAuthService implements IAuthService {
  async checkSession(): Promise<User | null> {
    try {
      console.log('🔐 Checking Amplify session...');
      const { getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth');

      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (currentUser && idToken) {
        console.log('👤 Amplify user authenticated:', currentUser.username);
        return {
          sub: currentUser.userId,
          email: currentUser.signInDetails?.loginId || '',
          name: currentUser.username,
          tier: 'free'
        };
      }

      console.log('⚠️ No Amplify session');
      return null;
    } catch (err) {
      console.log('⚠️ No Amplify session');
      return null;
    }
  }

  async login(email: string, password: string, _rememberMe = false): Promise<LoginResult> {
    console.log('🔑 Attempting Cognito login for:', email);
    const { signIn } = await import('aws-amplify/auth');

    const { isSignedIn, nextStep } = await signIn({
      username: email,
      password
    });

    if (isSignedIn) {
      console.log('✅ Cognito login successful');
      // Get user info after successful login
      const user = await this.checkSession();
      if (!user) {
        throw new Error('Failed to get user info after login');
      }
      return { user };
    } else {
      throw new Error(`Login incomplete: ${nextStep.signInStep}`);
    }
  }

  async signup(email: string, password: string, name?: string): Promise<SignupResult> {
    console.log('📝 Attempting Cognito signup for:', email);
    const { signUp } = await import('aws-amplify/auth');

    const { isSignUpComplete, nextStep } = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          ...(name && { name })
        }
      }
    });

    if (isSignUpComplete) {
      console.log('✅ Cognito signup successful - auto-confirmed');
      return { requiresVerification: false, email };
    } else if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
      console.log('📧 Cognito signup requires email verification');
      return { requiresVerification: true, email };
    } else {
      throw new Error(`Unexpected signup step: ${nextStep.signUpStep}`);
    }
  }

  async logout(): Promise<void> {
    console.log('🚪 Logging out from Cognito...');
    const { signOut } = await import('aws-amplify/auth');
    await signOut();
    console.log('✅ Cognito logout successful');
  }

  async getToken(): Promise<string | null> {
    try {
      console.log('🔑 CognitoAuthService: Fetching auth token...');
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || null;
      console.log('🔑 CognitoAuthService: Token retrieved:', token ? '✅ YES' : '❌ NO');
      if (!token) {
        console.error('❌ CognitoAuthService: No ID token in session. Session:', session);
      }
      return token;
    } catch (error) {
      console.error('❌ CognitoAuthService: Error fetching token:', error);
      return null;
    }
  }

  // Cognito handles token refresh automatically via fetchAuthSession
  async refreshToken(): Promise<string | null> {
    return this.getToken();
  }
}
